"use client";
import { useT } from "@/app/i18n/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { authClient } from "@/lib/client/betterauth/auth-client";
import { cn } from "@/lib/utils";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import LoginEmailOtp from "./LoginEmailOtp";
import LoginGoogle from "./LoginGoogle";
import LoginMicrosoft from "./LoginMicrosoft";
import { Separator } from "./ui/separator";

type AuthErrorType =
  | "Configuration"
  | "AccessDenied"
  | "Verification"
  | "Default"
  | "NoAccount"
  | "userdisabled"
  | "signup_disabled"
  | "please_restart_the_process"
  | "unable_to_create_user";

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const { t } = useT("signIn", {});

  function LoginErrorContent() {
    const searchParams = useSearchParams();
    const error = searchParams.get("error") as AuthErrorType | null;

    if (!error) return null;

    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("errors.alertTitle")}</AlertTitle>
        <AlertDescription>
          {t(`errors.serverErrors.${error}`) ||
            t("errors.serverErrors.Default")}
        </AlertDescription>
      </Alert>
    );
  }

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isLinkSent && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLinkSent, countdown]);

  const handleResend = async () => {
    if (!canResend) return;

    setIsResending(true);
    try {
      await authClient.signIn.magicLink({
        email: userEmail,
        callbackURL: "/app",
      });
      setCountdown(30);
      setCanResend(false);
    } catch (error) {
      console.error("Error resending magic link:", error);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignIn = () => {
    setIsLinkSent(false);
    setUserEmail("");
  };

  if (isLinkSent) {
    return (
      <div
        className={cn("flex flex-col gap-6 max-w-md mx-auto", className)}
        {...props}
      >
        <Card className="shadow-md">
          <CardHeader className="space-y-1 pb-6 text-center">
            <CardTitle className="text-2xl font-semibold">
              {t("magicLink.checkInbox")}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              {t("magicLink.loginLinkSent")} {userEmail}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              {t("magicLink.clickLinkInbox")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("magicLink.didntGetIt")}
            </p>
            <Button
              className="mt-4"
              onClick={handleResend}
              disabled={!canResend || isResending}
            >
              {isResending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("magicLink.resending")}
                </>
              ) : canResend ? (
                t("magicLink.resendLink")
              ) : (
                `${t("magicLink.resendIn")} ${countdown}s`
              )}
            </Button>
            <div className="mt-4">
              <Button
                variant="ghost"
                onClick={handleBackToSignIn}
                className="text-sm font-medium"
              >
                <ArrowLeft className=" h-4 w-4" /> {t("magicLink.backToSignIn")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={cn("flex flex-col gap-6 max-w-md mx-auto", className)}
      {...props}
    >
      <Card className="shadow-md">
        <CardHeader className="space-y-1 pb-6 text-center">
          <CardTitle className="text-2xl font-semibold">
            {t("welcomeBack")}
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            {t("loginWithAccount")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Suspense>
            <LoginErrorContent />
          </Suspense>
          <div className="flex flex-col gap-3">
            <LoginGoogle />
            <LoginMicrosoft />
          </div>
          <div>
            <div className="relative flex items-center justify-center">
              <Separator className="w-full" />
              <span className="absolute bg-background px-4 text-xs uppercase text-muted-foreground">
                {t("orWithEmail")}
              </span>
            </div>
            <div className="mt-4 space-y-6">
              {/* <div className="space-y-3">
                <LoginMagicLink onLinkSent={handleLinkSent} />
              </div> */}
              <div className="space-y-3">
                <LoginEmailOtp />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
        {t("termsAndPrivacy")} <Link href="/cgu">{t("termsOfService")}</Link>{" "}
        and <Link href="/privacy-policy">{t("privacyPolicy")}</Link>.
      </div>
    </div>
  );
}
